import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Plus, Check, ArrowLeft, Globe, Radio, Gamepad2, Crown, Lock, MonitorX, Tv } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { hlsProxy } from "@/lib/api";
import { VideoPlayer } from "@/components/VideoPlayer";
import { ContentCard } from "@/components/ContentCard";
import { useDeadlineCountdown } from "@/components/Countdown";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";

function uid() { return "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36); }

export default function Watch() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [startAt, setStartAt] = useState(0);
  const [stream, setStream] = useState(null);
  const [gate, setGate] = useState(false);
  const [signupGate, setSignupGate] = useState(false);
  const [limit, setLimit] = useState(false);
  const { user, subscribed, onTrial, accessUntil, activeProfile, isFavorite, toggleFavorite } = useAuth();
  const { display: priceDisplay, freeAccess } = useCurrency();
  const { t } = useTranslation();
  const { text: trialLeft } = useDeadlineCountdown(onTrial ? accessUntil : null);
  const sessionRef = useRef(uid());

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/content/${id}`).then(({ data }) => {
      setItem(data);
      let ep;
      if (data.type === "game") ep = api.get("/games", { params: { genre: data.category_label, limit: 14 } });
      else if (data.type === "radio") ep = api.get("/radio", { params: { region: data.category, limit: 14 } });
      else if (data.type === "series") ep = api.get("/series", { params: { genre: data.category_label, limit: 14 } });
      else ep = api.get("/channels", { params: { category: data.category, limit: 14 } });
      ep.then(({ data: r }) => setRelated(r.items.filter((x) => x.id !== id)));
    }).catch(() => setItem(false));
  }, [id]);

  useEffect(() => {
    if (!item) return;
    setStream(null); setGate(false); setLimit(false); setSignupGate(false);
    const params = { session_id: sessionRef.current, profile_id: activeProfile?.id || "" };
    api.get(`/play/${id}`, { params })
      .then(({ data }) => setStream(data))
      .catch((e) => {
        const s = e.response?.status;
        if (s === 402) setGate(true);
        else if (s === 429) setLimit(true);
        else if (s === 401) { if (freeAccess) setSignupGate(true); else nav("/login"); }
      });
  }, [item?.id, subscribed, id, nav, activeProfile, freeAccess]);

  // keep the stream session alive + release on leave (enforces 3-screen limit)
  useEffect(() => {
    if (!stream) return;
    const sid = sessionRef.current;
    const hb = setInterval(() => {
      api.post("/stream/heartbeat", { session_id: sid, content_id: id }).catch(() => {});
    }, 20000);
    return () => {
      clearInterval(hb);
      api.post("/stream/stop", { session_id: sid, content_id: id }).catch(() => {});
    };
  }, [stream, id]);

  const onProgress = (progress, duration) => {
    if (user && ["movie", "series"].includes(item?.type) && progress > 0) {
      api.post("/me/history", { content_id: id, progress, duration }).catch(() => {});
    }
  };

  const onFav = async () => {
    if (!user) { toast.error("Sign in to save"); nav("/login"); return; }
    const added = await toggleFavorite(item.id);
    toast.success(added ? "Added to My List" : "Removed from My List");
  };

  if (item === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-brand" size={40} /></div>;
  if (item === false) return <div className="min-h-screen grid place-items-center text-zinc-400 pt-16">Content not found.</div>;

  const fav = isFavorite(item.id);

  return (
    <div className="pt-20 pb-20 px-4 sm:px-8 lg:px-12 min-h-screen" data-testid="watch-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-zinc-400 hover:text-white mb-4 text-sm">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="max-w-5xl mx-auto">
        {onTrial && !freeAccess && !gate && !limit && (
          <div data-testid="trial-nudge"
            className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-gold/30 bg-gradient-to-r from-gold/15 via-amber-500/10 to-transparent px-4 py-3">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-gold/25 text-gold shrink-0"><Crown size={18} /></span>
            <p className="flex-1 text-sm text-zinc-200">
              {t("promo.trialWatch", { time: trialLeft || "…" })}
            </p>
            <button data-testid="trial-nudge-cta" onClick={() => nav("/subscribe")}
              className="shrink-0 inline-flex items-center gap-1.5 bg-gradient-to-r from-gold to-amber-500 text-black font-bold text-sm px-5 py-2 rounded-full hover:brightness-110 transition">
              <Crown size={15} /> {t("promo.pickPlan")}
            </button>
          </div>
        )}
        {signupGate ? (
          <div data-testid="signup-gate" className="relative w-full aspect-video rounded-xl overflow-hidden grid place-items-center text-center bg-gradient-to-br from-[#1a1a20] to-black border border-emerald-500/30 px-6">
            {item.backdrop && <img src={item.backdrop} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />}
            <div className="relative">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold mb-4">{t("free.badge")}</span>
              <h3 className="font-head text-2xl font-extrabold mb-2">{t("free.gateTitle")}</h3>
              <p className="text-zinc-400 text-sm max-w-sm mx-auto mb-6">{t("free.gateBody")}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button data-testid="signup-gate-register-btn" onClick={() => nav("/register")}
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-black font-bold px-7 py-3 rounded-lg hover:bg-emerald-400 transition-colors">
                  {t("free.cta")}
                </button>
                <button data-testid="signup-gate-login-btn" onClick={() => nav("/login")}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-white/10 hover:bg-white/20 font-semibold transition-colors">
                  {t("free.haveAccount")}
                </button>
              </div>
            </div>
          </div>
        ) : gate ? (
          <div data-testid="premium-gate" className="relative w-full aspect-video rounded-xl overflow-hidden grid place-items-center text-center bg-gradient-to-br from-[#1a1a20] to-black border border-gold/30 px-6">
            {item.backdrop && <img src={item.backdrop} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />}
            <div className="relative">
              <span className="w-14 h-14 grid place-items-center rounded-full bg-gold/20 text-gold mx-auto mb-4"><Lock size={26} /></span>
              <h3 className="font-head text-2xl font-extrabold mb-2">Subscription required</h3>
              <p className="text-zinc-400 text-sm max-w-sm mx-auto mb-6">This content is for subscribers only. Subscribe to unlock all live channels and games.</p>
              <button data-testid="gate-subscribe-btn" onClick={() => nav("/subscribe")}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-gold to-amber-500 text-black font-bold px-7 py-3 rounded-lg hover:brightness-110 transition">
                <Crown size={18} /> Subscribe — {priceDisplay}/mo
              </button>
            </div>
          </div>
        ) : limit ? (
          <div data-testid="stream-limit-gate" className="relative w-full aspect-video rounded-xl overflow-hidden grid place-items-center text-center bg-gradient-to-br from-[#1a1a20] to-black border border-brand/30 px-6">
            <div>
              <span className="w-14 h-14 grid place-items-center rounded-full bg-brand/20 text-brand mx-auto mb-4"><MonitorX size={26} /></span>
              <h3 className="font-head text-2xl font-extrabold mb-2">You're watching on 3 screens</h3>
              <p className="text-zinc-400 text-sm max-w-sm mx-auto mb-6">Your plan allows 3 simultaneous streams. Stop one to start watching here.</p>
              <button onClick={() => nav(-1)} className="bg-white/10 hover:bg-white/20 font-semibold px-6 py-3 rounded-lg transition">Go back</button>
            </div>
          </div>
        ) : stream && stream.stream_type === "game" ? (
          <div className="w-full aspect-video rounded-xl overflow-hidden bg-black">
            <iframe data-testid="game-frame" title={item.title} src={stream.embed_url}
              className="w-full h-full" allowFullScreen allow="autoplay; fullscreen; gamepad" />
          </div>
        ) : stream && stream.stream_type === "radio" ? (
          <div data-testid="radio-player" className="w-full aspect-video rounded-xl overflow-hidden relative grid place-items-center bg-gradient-to-br from-sky-900/40 via-[#10131a] to-black border border-sky-500/20">
            {item.poster && <img src={item.poster} alt="" className="absolute inset-0 w-full h-full object-contain p-16 opacity-10" />}
            <div className="relative text-center px-6 w-full max-w-md">
              <span className="w-20 h-20 grid place-items-center rounded-full bg-sky-500/20 text-sky-400 mx-auto mb-5 animate-pulse"><Radio size={40} /></span>
              <h2 className="font-head text-2xl font-extrabold mb-1">{item.title}</h2>
              <p className="text-zinc-400 text-sm mb-6">{item.country ? `${item.country} · ` : ""}{item.quality}</p>
              <audio data-testid="radio-audio" src={stream.stream_url} controls autoPlay className="w-full" />
            </div>
          </div>
        ) : stream ? (
          <VideoPlayer
            src={stream.stream_type === "hls" ? hlsProxy(stream.stream_url, stream.ua, stream.ref) : stream.stream_url}
            type={stream.stream_type} poster={item.backdrop} onProgress={onProgress} startAt={startAt} />
        ) : (
          <div className="w-full aspect-video rounded-xl bg-black grid place-items-center"><Loader2 className="animate-spin text-brand" size={40} /></div>
        )}

        <div className="mt-6 flex flex-col sm:flex-row sm:items-start gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              {item.type === "live"
                ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-brand animate-pulse"><Radio size={12} /> LIVE</span>
                : item.type === "radio"
                ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-sky-500 text-white"><Radio size={12} /> RADIO</span>
                : item.type === "series"
                ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-violet-600 text-white"><Tv size={12} /> SERIES</span>
                : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-pink text-white"><Gamepad2 size={12} /> GAME</span>}
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white/10">{item.category_label}</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-white/10">{item.quality}</span>
            </div>
            <h1 className="font-head text-2xl sm:text-4xl font-extrabold mb-3">{item.title}</h1>
            <div className="flex items-center gap-3 text-sm text-zinc-400 mb-4">
              {item.year && <span>{item.year}</span>}
              {item.country && <span className="flex items-center gap-1"><Globe size={13} /> {item.country}</span>}
              <span className="flex items-center gap-1">Source: {item.source}</span>
            </div>
            {item.type === "live" && item.epg && (
              <div data-testid="epg-guide" className="mb-4 bg-white/5 border border-white/10 rounded-xl p-4 max-w-md">
                <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">TV Guide · On Now</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold">{item.epg.now.title}</span>
                  <span className="text-zinc-500">{item.epg.now.start}–{item.epg.now.end}</span>
                </div>
                <div className="h-1.5 bg-white/15 rounded-full my-2 overflow-hidden"><div className="h-full bg-brand" style={{ width: `${item.epg.now.progress}%` }} /></div>
                <div className="flex items-center justify-between text-sm text-zinc-400">
                  <span>Up Next: {item.epg.next.title}</span><span className="text-zinc-600">{item.epg.next.start}</span>
                </div>
              </div>
            )}
            <p className="text-zinc-300 leading-relaxed max-w-2xl">{item.description}</p>
          </div>
          <button data-testid="watch-fav-btn" onClick={onFav}
            className="flex items-center justify-center gap-2 bg-white/10 border border-white/20 font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition-colors shrink-0">
            {fav ? <Check size={18} className="text-brand" /> : <Plus size={18} />} {fav ? "In My List" : "My List"}
          </button>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-14 max-w-6xl mx-auto">
          <h2 className="font-head text-xl font-bold mb-4">More like this</h2>
          <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2">
            {related.map((r) => <ContentCard key={r.id} item={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
